import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { isNetworkError } from '../../utils/errorHandler';
import { authApiClient } from '../../authApiClient';
import { trackSignupStarted } from '../../utils/analytics';

const COUNTRY_CODES = [
  { code: '+48', name: 'Poland', phoneLength: 9 },
  { code: '+995', name: 'Georgia', phoneLength: 9 },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+48');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const authMode = (location.state as { authMode?: 'login' | 'signup' } | undefined)?.authMode;

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and max digits based on selected country
    const maxLength = selectedCountry.phoneLength;
    if (new RegExp(`^\\d{0,${maxLength}}$`).test(value)) {
      setPhone(value);
      setError('');
    }
  };

  const handleCountryCodeChange = (e: any) => {
    setCountryCode(e.target.value);
    setPhone(''); // Clear phone when country changes
    setError('');
  };

  const getErrorMessage = (errorMessage: string): string => {
    // Map backend error messages to translation keys
    if (errorMessage.includes('User not found')) {
      return t('auth.userNotFound');
    }
    if (errorMessage.includes('Invalid credentials')) {
      return t('auth.invalidCredentials');
    }
    if (errorMessage.includes('User account not confirmed')) {
      return t('auth.userNotConfirmed');
    }
    if (errorMessage.includes('Invalid OTP code')) {
      return t('auth.invalidOtpCode');
    }
    if (errorMessage.includes('OTP code has expired')) {
      return t('auth.otpCodeExpired');
    }
    if (errorMessage.includes('Invalid or expired refresh token')) {
      return t('auth.invalidRefreshToken');
    }
    
    // Handle network errors
    if (isNetworkError(errorMessage)) {
      return t('auth.networkError');
    }
    
    // Handle server errors
    if (errorMessage.includes('Internal server error') || errorMessage.includes('500')) {
      return t('auth.serverError');
    }
    
    // Default fallback
    return errorMessage || t('auth.unknownError');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate phone number length
    if (phone.length !== selectedCountry.phoneLength) {
      setError(t('auth.phoneNumberLengthError', `Phone number must be ${selectedCountry.phoneLength} digits`));
      setIsLoading(false);
      return;
    }

    try {
      const fullPhoneNumber = `${countryCode}${phone}`;
      await authApiClient.initiateLogin({ phoneNumber: fullPhoneNumber });
      trackSignupStarted('account', { phone_country: countryCode });
      // Preserve redirect and booking state if provided
      const state = location.state as any;
      navigate('/verify-code', { 
        state: { 
          phone: fullPhoneNumber,
          verificationType: 'auth',
          redirectUrl: state?.redirectUrl,
          bookingState: {
            openBookingRoomId: state?.openBookingRoomId,
            roomAvailableFromTime: state?.roomAvailableFromTime,
            durationInMinutes: state?.durationInMinutes,
            openRoomBooking: state?.openRoomBooking,
            openTutorBooking: state?.openTutorBooking,
            lessonOptionId: state?.lessonOptionId,
            startTime: state?.startTime,
          }
        } 
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(getErrorMessage(errorMessage));
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title={authMode === 'signup' ? t('auth.createAccount', 'Create account') : t('auth.signIn', 'Sign In')}
        noindex={true}
      />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {authMode === 'signup' ? t('auth.createAccount', 'Create account') : t('auth.signIn', 'Sign In')}
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
              {t(
                'auth.signInOrCreateAccount',
                "We'll send a one-time code by SMS. Use it to sign in or finish setting up your account.",
              )}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <Select
                      value={countryCode}
                      onChange={handleCountryCodeChange}
                      disabled={isLoading}
                      sx={{ height: '56px' }}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    required
                    fullWidth
                    label={t('auth.phoneNumber', 'Phone Number')}
                    value={phone}
                    onChange={handlePhoneChange}
                    autoComplete="tel"
                    helperText={t('auth.phoneHelp', `Enter ${selectedCountry.phoneLength} digits after ${countryCode}`)}
                    error={!!error}
                    disabled={isLoading}
                  />
                </Grid>
              </Grid>
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ mb: 2 }}
              >
                {isLoading ? t('common.loading', 'Loading...') : t('auth.sendCode', 'Send Code')}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/')}
                disabled={isLoading}
              >
                {t('common.back', 'Back')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default LoginPage; 